/**
 * Builds the HTML that a crawler or link preview reads, before any script runs. The app is a single-page app, so
 * without this every address returns the home page's tags. One table (publicPages.json) drives this, the tags the
 * browser sets as you move around (SEOHead), the sitemap and robots.txt, so they cannot disagree.
 * Run after the build by scripts/prerender.mjs; tested in prerender.test.ts.
 */

export interface PublicPage {
  path: string;
  title: string;
  description: string;
  /** Language of the page. English when absent. */
  lang?: 'en' | 'ar';
  /** For a translation: the path of the English page it translates. */
  translationOf?: string;
  indexable: boolean;
  changefreq: string;
  priority: string;
  /** The day the page's content last changed (yyyy-mm-dd). Used as the sitemap date. */
  updated: string;
  summary: { h1: string; text: string; links: { label: string; href: string }[] };
}

export interface SeoData {
  /** The Arabic pages are built and tested but held back. Set to true in publicPages.json to publish them. */
  arabic?: boolean;
  site: string;
  brand: string;
  ogImage: string;
  pages: PublicPage[];
  disallow: string[];
}

export const escapeHtml = (value: string): string =>
  String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const urlOf = (site: string, path: string) => (path === '/' ? `${site}/` : `${site}${path}`);

/** Replaces one tag, matching it however the template wraps its attributes. Throws if the tag is missing. */
function replaceTag(html: string, pattern: RegExp, replacement: string, what: string): string {
  if (!pattern.test(html)) throw new Error(`The template has no ${what} to replace.`);
  return html.replace(pattern, replacement);
}

const meta = (attr: 'name' | 'property', key: string) =>
  new RegExp(`<meta\\s+${attr}="${key}"\\s+content="[^"]*"\\s*/?>`);

/** The pages that are live: every English page, and the Arabic ones only when the Arabic switch is on. */
export const livePages = (seo: SeoData): PublicPage[] => seo.pages.filter((p) => p.lang !== 'ar' || seo.arabic === true);

export interface Alternate { lang: string; url: string }

/**
 * The language versions of a page: itself, its translation or the page it translates, and x-default pointing at the English
 * page. Empty when the page has no translation, so such a page gets no hreflang at all.
 */
export function alternatesOf(page: PublicPage, seo: SeoData): Alternate[] {
  const live = livePages(seo);
  const english = page.translationOf ? live.find((p) => p.path === page.translationOf) : page;
  if (!english) return [];
  const translations = live.filter((p) => p.translationOf === english.path);
  if (translations.length === 0) return [];
  return [
    { lang: 'en', url: urlOf(seo.site, english.path) },
    ...translations.map((t) => ({ lang: t.lang ?? 'en', url: urlOf(seo.site, t.path) })),
    { lang: 'x-default', url: urlOf(seo.site, english.path) },
  ];
}

export function renderPageHtml(template: string, page: PublicPage, seo: SeoData): string {
  const url = urlOf(seo.site, page.path);
  const title = escapeHtml(page.title);
  const description = escapeHtml(page.description);
  let html = template;

  const lang = page.lang ?? 'en';
  html = html.replace(/<html lang="[^"]*"(?: dir="[^"]*")?>/, lang === 'ar' ? '<html lang="ar" dir="rtl">' : `<html lang="${lang}">`);
  html = replaceTag(html, /<title>[\s\S]*?<\/title>/, `<title>${title}</title>`, 'title');
  const tags: [RegExp, string, string][] = [
    [meta('name', 'description'), `<meta name="description" content="${description}">`, 'description'],
    [meta('name', 'robots'), `<meta name="robots" content="${page.indexable ? 'index, follow' : 'noindex, nofollow'}">`, 'robots tag'],
    [meta('property', 'og:title'), `<meta property="og:title" content="${title}">`, 'og:title'],
    [meta('property', 'og:description'), `<meta property="og:description" content="${description}">`, 'og:description'],
    [meta('property', 'og:url'), `<meta property="og:url" content="${url}">`, 'og:url'],
    [meta('name', 'twitter:title'), `<meta name="twitter:title" content="${title}">`, 'twitter:title'],
    [meta('name', 'twitter:description'), `<meta name="twitter:description" content="${description}">`, 'twitter:description'],
    [meta('name', 'twitter:url'), `<meta name="twitter:url" content="${url}">`, 'twitter:url'],
    [/<link\s+rel="canonical"\s+href="[^"]*"\s*\/?>/, `<link rel="canonical" href="${url}">`, 'canonical link'],
  ];
  for (const [pattern, replacement, what] of tags) html = replaceTag(html, pattern, replacement, what);
  // The generic title tag is optional; keep it in step when it is there.
  html = html.replace(meta('name', 'title'), `<meta name="title" content="${title}">`);

  const links = page.summary.links.map((l) => `<li><a href="${escapeHtml(l.href)}">${escapeHtml(l.label)}</a></li>`).join('');
  const body = `<main data-prerender>
      <h1>${escapeHtml(page.summary.h1)}</h1>
      <p>${escapeHtml(page.summary.text)}</p>
      <ul>${links}</ul>
    </main>`;
  html = replaceTag(html, /<div id="root">/, `<div id="root">${body}`, 'root element');
  const alternates = alternatesOf(page, seo);
  if (alternates.length) {
    const tags = alternates.map((a) => `    <link rel="alternate" hreflang="${a.lang}" href="${a.url}">`).join('\n');
    html = replaceTag(html, /<\/head>/, `${tags}\n</head>`, 'head end');
  }
  return html;
}

/**
 * The page served for every address that is not a public page (the signed-in app). Not indexable and with no
 * canonical address, so the app never borrows the home page's tags. The app sets its own once it loads.
 */
export function renderAppShell(template: string): string {
  let html = replaceTag(template, meta('name', 'robots'), '<meta name="robots" content="noindex, nofollow">', 'robots tag');
  html = html.replace(/\s*<link\s+rel="canonical"\s+href="[^"]*"\s*\/?>/, '');
  return html;
}

export function buildSitemap(seo: SeoData): string {
  const urls = livePages(seo)
    .filter((p) => p.indexable)
    .map((p) => {
      const alts = alternatesOf(p, seo).map((a) => `
    <xhtml:link rel="alternate" hreflang="${a.lang}" href="${a.url}"/>`).join('');
      return `
  <url>
    <loc>${urlOf(seo.site, p.path)}</loc>
    <lastmod>${p.updated}</lastmod>
    <changefreq>${p.changefreq}</changefreq>
    <priority>${p.priority}</priority>${alts}
  </url>`;
    })
    .join('');
  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">${urls}
</urlset>
`;
}

export function buildRobots(seo: SeoData): string {
  return `# ${seo.brand}: the public pages are indexed; the app is not.
User-agent: *
Allow: /

${seo.disallow.map((d) => `Disallow: ${d}`).join('\n')}

Sitemap: ${seo.site}/sitemap.xml
`;
}
